"use client";

import { useEffect, useMemo, useRef } from "react";
import { usePublicFrontendTranslation } from "@smartmanager/shell-ui/shared/i18n/usePublicFrontendTranslation";
import { useReportRenderBoundaryError } from "@/src/client/observability/renderBoundaryError";

type RouteErrorPanelProps = {
  error: Error & { digest?: string };
  reset: () => void;
  /** The Next.js boundary that caught the error, for error monitoring. */
  source: "global-error-boundary" | "route-error-boundary";
};

export default function RouteErrorPanel({ error, reset, source }: RouteErrorPanelProps) {
  const t = usePublicFrontendTranslation();
  useReportRenderBoundaryError(source, error);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const errorReference = useMemo(
    () => error.digest?.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64) || null,
    [error.digest],
  );

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <main className="flex h-screen h-[100dvh] w-full items-center justify-center overflow-y-auto bg-[#020813] px-5 py-10 text-white">
      <section
        aria-labelledby="route-error-heading"
        aria-describedby="route-error-description"
        className="w-full max-w-lg rounded-3xl border border-cyan-200/20 bg-slate-950/95 p-7 text-center shadow-[0_28px_90px_rgba(0,0,0,0.55)] sm:p-9"
      >
        <div aria-hidden="true" className="mx-auto mb-6 h-12 w-12 rounded-full border border-cyan-200/35 bg-cyan-300/10 shadow-[0_0_32px_rgba(34,211,238,0.2)]" />
        <h1
          ref={headingRef}
          id="route-error-heading"
          tabIndex={-1}
          className="text-2xl font-semibold tracking-tight outline-none"
        >
          {t("Smart Manager needs a moment")}
        </h1>
        <p id="route-error-description" role="alert" className="mt-3 text-sm leading-6 text-slate-300">
          {t("This view could not be restored safely. Try loading it again; your account session will remain protected.")}
        </p>
        {errorReference && (
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500">
            {t("Reference {reference}", { reference: errorReference })}
          </p>
        )}
        <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={reset}
            className="min-h-11 rounded-full border border-cyan-200/35 bg-cyan-300/15 px-6 text-sm font-semibold text-cyan-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-200"
          >
            {t("Try again")}
          </button>
          <button
            type="button"
            // A full document load: a router push is a no-op for this boundary
            // when the failed view is home, and home should start clean anyway.
            // in-app-browser exception: a first-party Smart Manager page in this same window, not a website.
            onClick={() => window.location.replace("/")}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/15 bg-white/5 px-6 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
          >
            {t("Return home")}
          </button>
        </div>
      </section>
    </main>
  );
}
